import { CloseIcon, LightningBoltIcon } from "@worship-devo/ui/icons";
import type { JsonType } from "posthog-js";
import posthogClient from "posthog-js";
import { analytics } from "~/analytics";
import { showCustomToast, ToastTestIds } from "~/components/showCustomToast";
import { type Devo, getDevoPlaybackIdentity, usePlayerStore } from "~/hooks/usePlayerStore";
import { PROGRESS_EVENT_NAME, trackDevoAudioEvent } from "~/lib/analytics.utils";
import { getLoadSequenceId } from "~/lib/audioSequencePlan";
import { EPISODE_COMPLETE_THRESHOLD_PERCENTAGE, STREAK_TOAST_THRESHOLD_PERCENTAGE } from "~/lib/constants";
import {
  computeEpisodePercentage,
  getActiveEpisodePlaybackLayout,
  getEpisodePlaybackProgress,
  getSequencePositionForEpisodePercentage,
  getSequencePositionForPlaybackRecovery,
} from "~/lib/episode.utils";
import { checkFirstWeekThreeDayCompletion } from "~/lib/firstWeekCompletionAnalytics";
import { playerEngine } from "~/lib/playerEngine";
import { getWinningPostMainPrompt } from "~/lib/postMainPrompt";
import {
  getStreakCelebrationMilestone,
  getStreakToastSuppressionDay,
  hasConfiguredStreakMilestones,
  hasCrossedCompletionThreshold,
  projectPostCompletionStreak,
  type StreakCelebrationMilestone,
} from "~/lib/streakCelebration";
import { hasCompletedNameProfile } from "~/lib/user.utils";
import { FEATURE_FLAGS, MULTIVARIATE_FLAG_CONFIG } from "~/posthog/featureFlags";
import { ProgressClientService } from "~/services/progress.client";
import { StreakClientService } from "~/services/streak.client";
import { ensureProgressData } from "~/stores/reactQuery/useProgressQuery";
import { ensureUserData } from "~/stores/reactQuery/useUserQuery";
import { checkIsInNameStep, subscribeToNameStepState } from "~/stores/useNameStepStateStore";

import { isAutoplayUnlocked, startAutoplayUnlockTracking, subscribeToAutoplayUnlock } from "./autoplayUnlock";
import { BookendImpressionTracker } from "./bookendImpressionTracker";
import {
  createInitialStreakWorkflowState,
  reduceStreakWorkflow,
  type StreakWorkflowState,
  shouldRequestProjectedStreak,
  shouldShowStreakToast,
} from "./streakWorkflow";

const ANALYTICS_INTERVAL = 120_000;
const IMPRESSION_CHECK_INTERVAL = 200;
const backendSyncEnvVal = Number(import.meta.env.VITE_PROGRESS_UPDATE_INTERVAL);
const BACKEND_SYNC_INTERVAL = Number.isFinite(backendSyncEnvVal) && backendSyncEnvVal > 0 ? backendSyncEnvVal : 30_000;
const SYNC_CHECK_INTERVAL = 5_000;

type StreakVariant = (typeof MULTIVARIATE_FLAG_CONFIG.STREAK_CELEBRATION_TOAST.variants)[number];

type RuntimeSubscriptions = {
  playerStore: (() => void) | null;
  playerEngine: (() => void) | null;
  autoplayUnlock: (() => void) | null;
  nameStep: (() => void) | null;
};

function getDevoKey(devo: Devo | undefined): string | null {
  if (!devo) {
    return null;
  }

  return getDevoPlaybackIdentity(devo);
}

function canAutoplayCurrentDevo(devo: Devo, user: Awaited<ReturnType<typeof ensureUserData>>): boolean {
  const autoplayParams = new URLSearchParams(window.location.search);

  if (autoplayParams.get("autoplay") === null || checkIsInNameStep()) {
    return false;
  }

  if (!user) {
    return false;
  }

  if (!hasCompletedNameProfile(user)) {
    return false;
  }

  if (user.hasDevoPlus) {
    return true;
  }

  return devo.canAutoplayForUser !== false;
}

function getActiveTrackLayout(devo: Devo) {
  return getActiveEpisodePlaybackLayout(devo, playerEngine.isUsingFallbackSequence());
}

class DevoPlaybackRuntimeController {
  private started = false;
  private currentDevoKey: string | null = null;
  private activePlaybackDevoKey: string | null = null;
  private lastAnalyticsPercentage = 0;
  private lastAnalyticsTime = 0;
  private completionLogged = false;
  private endedTracked = false;
  private streakWorkflow: StreakWorkflowState = createInitialStreakWorkflowState();
  private syncIntervalId: ReturnType<typeof setInterval> | null = null;
  private impressionIntervalId: ReturnType<typeof setInterval> | null = null;
  private autoplayTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private loadRequestId = 0;
  private bookendImpressionTracker = new BookendImpressionTracker({
    getDevo: () => usePlayerStore.getState().devo,
    getEngineState: () => playerEngine.getState(),
    getPosition: () => playerEngine.getPosition(),
    isUsingFallbackSequence: () => playerEngine.isUsingFallbackSequence(),
  });
  private subscriptions: RuntimeSubscriptions = {
    playerStore: null,
    playerEngine: null,
    autoplayUnlock: null,
    nameStep: null,
  };

  start() {
    if (this.started) {
      return;
    }

    this.started = true;
    startAutoplayUnlockTracking();
    this.subscriptions = {
      playerStore: usePlayerStore.subscribe(() => {
        this.handleDevoChange();
      }),
      playerEngine: playerEngine.subscribe(() => {
        this.handlePlayerEngineChange();
      }),
      autoplayUnlock: subscribeToAutoplayUnlock(() => {
        void this.syncPlaybackForCurrentDevo();
      }),
      nameStep: subscribeToNameStepState((isInNameStep) => {
        if (!isInNameStep) {
          void this.syncPlaybackForCurrentDevo();
        }
      }),
    };

    this.handleDevoChange();
    this.handlePlayerEngineChange();
  }

  resetForTests() {
    this.clearAutoplayTimeout();
    this.clearSyncInterval();
    this.clearImpressionInterval();
    this.subscriptions.playerStore?.();
    this.subscriptions.playerEngine?.();
    this.subscriptions.autoplayUnlock?.();
    this.subscriptions.nameStep?.();
    this.started = false;
    this.currentDevoKey = null;
    this.activePlaybackDevoKey = null;
    this.lastAnalyticsPercentage = 0;
    this.lastAnalyticsTime = 0;
    this.completionLogged = false;
    this.endedTracked = false;
    this.streakWorkflow = createInitialStreakWorkflowState();
    this.loadRequestId = 0;
    this.bookendImpressionTracker.resetAll();
    this.subscriptions = {
      playerStore: null,
      playerEngine: null,
      autoplayUnlock: null,
      nameStep: null,
    };
  }

  private getCurrentPercentage(): number {
    const { devo } = usePlayerStore.getState();
    const engineState = playerEngine.getState();
    const position = playerEngine.getPosition();

    if (!devo || !engineState.playable) {
      return 0;
    }

    return computeEpisodePercentage({
      progress: position,
      duration: engineState.duration,
      playable: engineState.playable,
      ...getActiveTrackLayout(devo),
    });
  }

  private clearAutoplayTimeout() {
    if (!this.autoplayTimeoutId) {
      return;
    }

    clearTimeout(this.autoplayTimeoutId);
    this.autoplayTimeoutId = null;
  }

  private clearSyncInterval() {
    if (!this.syncIntervalId) {
      return;
    }

    clearInterval(this.syncIntervalId);
    this.syncIntervalId = null;
  }

  private clearImpressionInterval() {
    if (!this.impressionIntervalId) {
      return;
    }

    clearInterval(this.impressionIntervalId);
    this.impressionIntervalId = null;
  }

  private getStreakCelebrationConfig(): { variant: StreakVariant; payload: JsonType | undefined } {
    const config = MULTIVARIATE_FLAG_CONFIG.STREAK_CELEBRATION_TOAST;
    const rawVariant = posthogClient.getFeatureFlag(FEATURE_FLAGS.STREAK_CELEBRATION_TOAST);

    const variant =
      typeof rawVariant === "string" && config.variants.includes(rawVariant as StreakVariant)
        ? (rawVariant as StreakVariant)
        : config.default;

    const payload = posthogClient.getFeatureFlagPayload(FEATURE_FLAGS.STREAK_CELEBRATION_TOAST) as JsonType | undefined;

    return { variant, payload };
  }

  private async trackStreakCelebrationEvent(
    event: string,
    milestone: StreakCelebrationMilestone,
    streakDay: number,
    variant: StreakVariant,
  ) {
    const { devo } = usePlayerStore.getState();
    let user: Awaited<ReturnType<typeof ensureUserData>> = null;

    try {
      user = await ensureUserData();
    } catch (_e) {
      // user data unavailable, keep defaults
    }

    const preferredSendTime = user?.preferredSendTime ?? null;
    const plan = user ? (user.hasDevoPlus ? "sing_plus" : "free") : null;

    analytics.track({
      event,
      properties: {
        streak_day: streakDay,
        milestone_day: milestone.day,
        milestone_title: milestone.title,
        milestone_subtitle: milestone.subtitle ?? null,
        devo_id: devo?.id ?? null,
        series_slug: devo?.series?.[0]?.slug?.current ?? null,
        flag_variant: variant,
        preferred_send_time: preferredSendTime,
        plan,
      },
    });
  }

  private maybeShowStreakCelebrationToast() {
    const { devo } = usePlayerStore.getState();
    const user = ensureUserData();
    const { variant, payload } = this.getStreakCelebrationConfig();

    if (
      !devo ||
      variant !== "test" ||
      !hasConfiguredStreakMilestones(payload) ||
      !shouldShowStreakToast(this.streakWorkflow)
    ) {
      return;
    }

    const projectedStreakDay = this.streakWorkflow.projectedStreakDay;
    if (projectedStreakDay === null) {
      return;
    }

    const milestone = getStreakCelebrationMilestone(payload, projectedStreakDay);
    if (!milestone) {
      return;
    }

    this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "toastShown" });

    showCustomToast({
      icon: LightningBoltIcon,
      message: milestone.title,
      description: milestone.subtitle,
      testId: ToastTestIds.STREAK_TOAST,
      type: "info",
      action: {
        icon: CloseIcon,
        ariaLabel: "Dismiss streak celebration toast",
        onClick: () => {
          void this.trackStreakCelebrationEvent(
            "Streak Celebration Toast Dismissed",
            milestone,
            projectedStreakDay,
            variant,
          );
        },
      },
      filled: true,
    });

    void user.then((resolvedUser) => {
      if (!resolvedUser) {
        return;
      }

      analytics.track({
        event: "Streak Celebration Toast Shown",
        properties: {
          streak_day: projectedStreakDay,
          milestone_day: milestone.day,
          milestone_title: milestone.title,
          milestone_subtitle: milestone.subtitle ?? null,
          devo_id: devo.id,
          series_slug: devo.series?.[0]?.slug?.current ?? null,
          flag_variant: variant,
          preferred_send_time: resolvedUser.preferredSendTime ?? null,
          plan: resolvedUser.hasDevoPlus ? "sing_plus" : "free",
          $set: {
            streak_toast_suppression_day: getStreakToastSuppressionDay({ timezone: resolvedUser.timezone }),
          },
        },
      });
    });
  }

  private requestProjectedStreak(userTimezone: string) {
    if (!shouldRequestProjectedStreak(this.streakWorkflow)) {
      return;
    }

    this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "completionThresholdCrossed" });

    void StreakClientService.getStreak()
      .then((streak) => {
        if (!streak) {
          this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "streakLoadFailed" });
          return;
        }

        const projectedStreakDay = projectPostCompletionStreak({
          currentStreak: streak.currentStreak,
          lastListenDate: streak.lastListenDate,
          timezone: userTimezone,
        });

        this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "streakLoaded", projectedStreakDay });
        this.maybeShowStreakCelebrationToast();
      })
      .catch(() => {
        this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "streakLoadFailed" });
      });
  }

  private markCompletionAndSync(percentage: number) {
    const { devo, markCompleted } = usePlayerStore.getState();

    if (!devo || this.completionLogged) {
      return;
    }

    this.completionLogged = true;
    markCompleted(devo.id);
    void ProgressClientService.logListenProgress(devo.id, percentage);
  }

  private async evaluateProgressThresholds(): Promise<void> {
    const { devo } = usePlayerStore.getState();
    const engineState = playerEngine.getState();

    if (!devo || !engineState.playable) {
      this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "progressObserved", percentage: 0 });
      return;
    }

    const user = await ensureUserData();
    const currentDevo = usePlayerStore.getState().devo;
    if (getDevoKey(currentDevo) !== getDevoKey(devo)) {
      return;
    }

    const currentEngineState = playerEngine.getState();
    if (!currentDevo || !currentEngineState.playable) {
      this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "progressObserved", percentage: 0 });
      return;
    }

    if (!user) {
      const percentage = this.getCurrentPercentage();
      this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "progressObserved", percentage });
      return;
    }

    const percentage = this.getCurrentPercentage();
    const previousPercentage = this.streakWorkflow.previousPercentage;
    const position = playerEngine.getPosition();
    const trackLayout = getActiveTrackLayout(currentDevo);
    const playbackProgress = getEpisodePlaybackProgress({
      progress: position,
      duration: currentEngineState.duration,
      playable: currentEngineState.playable,
      ...trackLayout,
    });
    const { variant, payload } = this.getStreakCelebrationConfig();
    const canEvaluateStreakCelebration = variant === "test" && hasConfiguredStreakMilestones(payload);
    const hasOutroAudio = playbackProgress.mainEndOffset < currentEngineState.duration;
    const winningPostMainPrompt = getWinningPostMainPrompt(currentDevo);
    const shouldDelayToastUntilMainEnd = hasOutroAudio || winningPostMainPrompt === "outro-video";
    const crossedIntoPostMainExperience =
      shouldDelayToastUntilMainEnd &&
      playbackProgress.mainDuration > 0 &&
      !this.streakWorkflow.toastThresholdReached &&
      playbackProgress.hasReachedMainEnd;

    if (hasCrossedCompletionThreshold(previousPercentage, percentage, EPISODE_COMPLETE_THRESHOLD_PERCENTAGE)) {
      this.markCompletionAndSync(percentage);
      void checkFirstWeekThreeDayCompletion({ user, includeCurrentCompletionDay: true });

      if (canEvaluateStreakCelebration) {
        this.requestProjectedStreak(user.timezone);
      }
    }

    if (crossedIntoPostMainExperience) {
      this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "toastThresholdCrossed" });
      this.maybeShowStreakCelebrationToast();
    } else if (
      !shouldDelayToastUntilMainEnd &&
      hasCrossedCompletionThreshold(previousPercentage, percentage, STREAK_TOAST_THRESHOLD_PERCENTAGE)
    ) {
      this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "toastThresholdCrossed" });
      this.maybeShowStreakCelebrationToast();
    }

    this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "progressObserved", percentage });
  }

  private async syncProgressAndAnalytics(): Promise<void> {
    const { devo, progressLastSent, setProgressLastSent } = usePlayerStore.getState();
    const engineState = playerEngine.getState();

    if (!devo || !engineState.playable || !engineState.playing) {
      return;
    }

    const position = playerEngine.getPosition();
    const percentage = this.getCurrentPercentage();
    const playbackProgress = getEpisodePlaybackProgress({
      progress: position,
      duration: engineState.duration,
      playable: engineState.playable,
      ...getActiveTrackLayout(devo),
    });
    const outsideTrailer = position >= playbackProgress.mainStartOffset;
    const now = Date.now();

    if (outsideTrailer && now - progressLastSent >= BACKEND_SYNC_INTERVAL) {
      void ProgressClientService.logListenProgress(devo.id, percentage);
      setProgressLastSent(now);
    }

    if (
      now - this.lastAnalyticsTime >= ANALYTICS_INTERVAL &&
      Math.abs(percentage - this.lastAnalyticsPercentage) >= 1
    ) {
      this.lastAnalyticsPercentage = percentage;
      this.lastAnalyticsTime = now;
      void trackDevoAudioEvent(PROGRESS_EVENT_NAME, {
        devo,
        position,
        duration: engineState.duration,
        percentage,
      });
    }
  }

  private updateSyncInterval() {
    if (!playerEngine.getState().playing) {
      this.clearSyncInterval();
      this.clearImpressionInterval();
      return;
    }

    if (!this.syncIntervalId) {
      this.syncIntervalId = setInterval(() => {
        void this.syncProgressAndAnalytics();
      }, SYNC_CHECK_INTERVAL);
    }

    if (!this.impressionIntervalId) {
      this.impressionIntervalId = setInterval(() => {
        this.bookendImpressionTracker.evaluate("read-in");
        this.bookendImpressionTracker.evaluate("read-out");
      }, IMPRESSION_CHECK_INTERVAL);
    }
  }

  private async syncPlaybackForCurrentDevo(): Promise<void> {
    const { devo, setMetadataLoaded, setTrailerOffsetSeconds } = usePlayerStore.getState();

    if (!devo) {
      return;
    }

    const loadRequestId = ++this.loadRequestId;
    const user = await ensureUserData();

    if (!user || loadRequestId !== this.loadRequestId) {
      return;
    }

    const newSequenceId = getLoadSequenceId(devo.tracks, devo.fallbackTracks);
    const currentSequenceId = playerEngine.getState().currentSequenceId;
    const isAlreadyLoaded = currentSequenceId === newSequenceId;
    const progressData = await ensureProgressData();

    if (loadRequestId !== this.loadRequestId) {
      return;
    }

    const progress = progressData.find((item) => item.cmsId === devo.id)?.progressPercentage;
    const autoplayParams = new URLSearchParams(window.location.search);
    const startParam = autoplayParams.get("start");
    const requestedProgress = isAutoplayUnlocked() ? (startParam === null ? progress : Number(startParam)) : 0;
    const startingProgress = Number.isFinite(requestedProgress) ? (requestedProgress ?? 0) : 0;
    const sequence = await playerEngine.loadSequence(devo.tracks, 0, devo.fallbackTracks, (context) => {
      if (context.previous) {
        return getSequencePositionForPlaybackRecovery({
          previous: {
            position: context.previous.position,
            duration: context.previous.duration,
            playable: context.previous,
            ...getActiveEpisodePlaybackLayout(devo, context.previous.usingFallbackSequence),
          },
          next: {
            duration: context.playable.getDuration(),
            playable: context.playable,
            ...getActiveEpisodePlaybackLayout(devo, context.usingFallbackSequence),
          },
        });
      }

      return getSequencePositionForEpisodePercentage({
        percentage: startingProgress,
        duration: context.playable.getDuration(),
        playable: context.playable,
        ...getActiveEpisodePlaybackLayout(devo, context.usingFallbackSequence),
      });
    });

    if (loadRequestId !== this.loadRequestId) {
      return;
    }

    setMetadataLoaded(Boolean(sequence));

    if (!sequence) {
      return;
    }

    const playbackBounds = getEpisodePlaybackProgress({
      progress: sequence.getPosition(),
      duration: sequence.getDuration(),
      playable: sequence,
      ...getActiveTrackLayout(devo),
    });
    setTrailerOffsetSeconds(playbackBounds.mainStartOffset);

    const shouldAutoplay =
      canAutoplayCurrentDevo(devo, user) &&
      isAutoplayUnlocked() &&
      (!isAlreadyLoaded || !playerEngine.getState().playing);

    if (!shouldAutoplay) {
      return;
    }

    this.clearAutoplayTimeout();
    this.autoplayTimeoutId = setTimeout(() => {
      void playerEngine.play();
    }, 300);
  }

  private handleDevoChange() {
    const { devo } = usePlayerStore.getState();
    const devoKey = getDevoKey(devo);

    if (devoKey === this.currentDevoKey) {
      return;
    }

    this.clearAutoplayTimeout();
    this.currentDevoKey = devoKey;
    this.lastAnalyticsPercentage = 0;
    this.lastAnalyticsTime = 0;
    this.completionLogged = false;
    this.endedTracked = false;
    this.bookendImpressionTracker.resetAll();
    this.streakWorkflow = reduceStreakWorkflow(this.streakWorkflow, { type: "devoChanged", devoId: devo?.id ?? null });

    if (!devo) {
      return;
    }

    void this.syncPlaybackForCurrentDevo();
  }

  private handlePlayerEngineChange() {
    this.updateSyncInterval();
    this.bookendImpressionTracker.evaluate("read-in");
    this.bookendImpressionTracker.evaluate("read-out");
    void this.evaluateProgressThresholds();

    const { devo } = usePlayerStore.getState();
    const engineState = playerEngine.getState();

    if (!devo || !engineState.playing) {
      this.activePlaybackDevoKey = null;
    } else {
      const devoKey = getDevoKey(devo);
      if (devoKey && devoKey !== this.activePlaybackDevoKey) {
        this.activePlaybackDevoKey = devoKey;
        void checkFirstWeekThreeDayCompletion();
      }
    }

    if (!devo || !engineState.ended || this.endedTracked) {
      return;
    }

    this.endedTracked = true;

    const percentage = this.getCurrentPercentage();
    this.markCompletionAndSync(percentage);
    void trackDevoAudioEvent("Audio Playback: Ended", {
      devo,
      progress: playerEngine.getPosition(),
      duration: engineState.duration,
      percentage,
    });
  }
}

const devoPlaybackRuntime = new DevoPlaybackRuntimeController();

export function startDevoPlaybackRuntime(): void {
  devoPlaybackRuntime.start();
}

export function resetDevoPlaybackRuntimeForTests(): void {
  devoPlaybackRuntime.resetForTests();
}
